/**
 * Basa full text: a chat link to a legal-research document opens the
 * document's full text, which the client reads from its server and the server
 * reads from the consultant service with the user's own inference credential.
 */
import * as Schema from "effect/Schema";
/**
 * The link the agent writes into a message for a document's full text,
 * `/basa/<base>/<n>`: `base` is the information bank, `n` the document number,
 * both as the search result names them.
 */
export const BASA_DOCUMENT_LINK_PATTERN = /^\/basa\/([A-Za-z0-9]{2,10})\/(\d{1,12})\/?$/;
/**
 * The server route the client reads a document from:
 * `GET /api/basa/documents/<base>/<n>?format=rtf|text`. Under `/api` so the dev
 * client's proxy forwards it to the server.
 */
export const BASA_DOCUMENTS_ROUTE_PREFIX = "/api/basa/documents";
export const BasaDocumentFormat = Schema.Literals(["rtf", "text"]);
/** The document a chat link names, or `null` for any other href. */
export function parseBasaDocumentLink(href) {
    const match = BASA_DOCUMENT_LINK_PATTERN.exec(href);
    if (match === null || match[1] === undefined || match[2] === undefined)
        return null;
    return { base: match[1].toUpperCase(), n: match[2] };
}
/** The server route that serves a document's full text in a format. */
export function basaDocumentRoutePath(address, format) {
    return `${BASA_DOCUMENTS_ROUTE_PREFIX}/${address.base}/${address.n}?format=${format}`;
}
/**
 * Why a document's full text did not arrive. The client localizes each reason;
 * the server sends no prose.
 *
 * - `invalid_address` (400): the address fails the link pattern, or the
 *   consultant service rejected it.
 * - `not_found` (404): the bank has no such document.
 * - `not_configured` (503): the deployment config names no legal-research
 *   endpoint. An unconfigured capability, shown as «не подключён».
 * - `unavailable` (503): a configured service failed — no inference credential
 *   held for the user, every seat busy, an upstream error, a timeout, or a
 *   network error. An outage, shown as «временно недоступен».
 */
export const BasaDocumentErrorCode = Schema.Literals([
    "invalid_address",
    "not_found",
    "not_configured",
    "unavailable",
]);
/** Body of every non-2xx answer the route itself produces. Auth refusals keep their own shape. */
export const BasaDocumentErrorResponse = Schema.Struct({
    error: BasaDocumentErrorCode,
});
